import { useEffect, useState } from "react";
import { fetchDeals } from "./dealThunk";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Input,
  Tooltip,
  Drawer,
  Typography,
  Grid,
} from "antd";
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  SearchOutlined,
  FilterOutlined,
  QuestionCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

import "../crmHeaderCard.css";
import "../crmSharedStyle.css";

import CreateDealModal from "./components/CreateDealModal";
import DealKanban from "./components/DealKanban";
import DealsDashboard from "./components/DealsDashboard";
import DealActivityModal from "./components/DealActivityModal";
import DealTable from "./components/DealTable";

import { getAccessType } from "../../../lib/functions";
import { selectAuthInfo } from "../../Auth/authSelectors";
import CreateDealDrawer from "./components/CreateDealDrawer";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

const Deals = () => {

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const dispatch = useDispatch();
  const { list, loading } = useSelector((s) => s.deals);  

  const [open, setOpen] = useState(false);
  const [passedStage, setPassedStage] = useState(null);
  const [view, setView] = useState("kanban");

  const [selectedDeal, setSelectedDeal] = useState(null);
  const [viewModal, setViewModal] = useState(false);

  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const authInfo = useSelector(selectAuthInfo);

  const navigate = useNavigate();

  useEffect(() => {
    getAccessType("crm_deals");
  }, []);

  useEffect(() => {
    dispatch(fetchDeals());
  }, [dispatch]);

  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(fetchDeals({ search }));
    }, 400);

    return () => clearTimeout(delay);
  }, [search]);

  const handleViewActivity = (deal) => {
    setSelectedDeal(deal);
    setViewModal(true);
  };

  const handleSearch = (value) => {
    setSearch(value);
    dispatch(fetchDeals({ search: value }));
  };

  const handleApplyFilters = (filters) => {
    dispatch(fetchDeals(filters));
    setFilterOpen(false);
  };

  return (
    <div className="p-4">

      {/* 🔥 HEADER */}
      <Card
      className="crm-header-card"
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)} 
                  style={{ marginRight: 8 }}
                />
          <Title level={4} style={{ margin: 0 }}>
            Deals
          </Title>
          </div>

          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 17 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      }
        extra={
          <div style={{ display: "flex", gap: !isMobile ? 8: 2 }}>
            {/* VIEW TOGGLE */}
            <Button
              type={view === "list" ? "primary" : "default"}
              icon={<UnorderedListOutlined />}
              onClick={() => setView("list")}
            />

            <Button
              type={view === "kanban" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setView("kanban")}
            />

            {/* CREATE */}
            <Button
              type="primary"
              onClick={() => {
                setPassedStage(null);
                setOpen(true);
              }}
            >
              + New Deal
            </Button>
          </div>
        }
      >
        {/* DASHBOARD */}
        <DealsDashboard authInfo={authInfo} deals={list} loading={loading} />

        {/* 🔍 SEARCH + FILTER */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <Input
            size="large"
            placeholder="Search deals..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />

          <Tooltip title="Filter">
            <Button
              size="large"
              icon={<FilterOutlined />}
              onClick={() => setFilterOpen(true)}
            />
          </Tooltip>
        </div>

        {/* VIEW */}
        <Card loading={loading}>
          {view === "kanban" ? (
            <DealKanban
              deals={list}
              onViewActivity={handleViewActivity}
              loading={loading}
              setOpen={(stage) => {
                setPassedStage(stage);
                setOpen(true);
              }}
            />
          ) : (
            <DealTable deals={list} onViewActivity={handleViewActivity}/>
          )}
        </Card>
      </Card>

      {/* MODALS
      <CreateDealModal
        open={open}
        onClose={() => setOpen(false)}
        passedStage={passedStage}
      /> */}

      <CreateDealDrawer
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={() => dispatch(fetchDeals())}
      />

      <DealActivityModal
        open={viewModal}
        onClose={() => setViewModal(false)}
        deal={selectedDeal}
      />

      {/* 🔥 (NEXT STEP) FILTER DRAWER */}
      {/* <DealFilterDrawer open={filterOpen} onApply={handleApplyFilters} /> */}
        <Drawer
          title="CRM Deals Help"
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          width={screens.md ? 430 : "100%"}
          className="crm-drawer"
          styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
        >
          <div style={{ lineHeight: 1.8 }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "12px",
                marginLeft: "12px"
              }}
            >
              CRM Deal Management
            </h2>

            <p style={{ color: "#555", marginBottom: "16px", marginLeft:17 }}>
              The CRM Deals module is used to manage sales opportunities,
              customer negotiations, pipeline tracking, and sales workflows
              within the ERP CRM system. It helps sales teams monitor deal progress,
              customer engagement, and revenue opportunities.
            </p>

            <div
              style={{
                background: "#f5f5f5",
                padding: "14px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <strong>Available Actions</strong>

              <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
                <li>View deals in Kanban View and List View</li>
                <li>Create new sales deals and opportunities</li>
                <li>Track deal stages visually using Kanban boards</li>
                <li>Search deals using live search functionality</li>
                <li>View customer deal activities and communications</li>
                <li>Monitor sales pipeline progress</li>
                <li>Track revenue opportunities and customer engagement</li>
                <li>Open detailed deal activity records</li>
                <li>Manage stage-wise deal movement</li>
                <li>View sales dashboards and analytics summaries</li>
              </ol>
            </div>

            <div
              style={{
                background: "#fafafa",
                border: "1px solid #eee",
                padding: "14px",
                borderRadius: "8px",
              }}
            >
              <strong>Important Notes</strong>

              <ul
                style={{
                  paddingLeft: "18px",
                  marginTop: "10px",
                  color: "#666",
                }}
              >
                <li>Kanban View helps track deal stages visually.</li>
                <li>List View provides detailed tabular deal management.</li>
                <li>Deal activities help maintain customer communication history.</li>
                <li>Search functionality updates deal data dynamically.</li>
                <li>Sales dashboards display pipeline and revenue summaries.</li>
                <li>Role permissions control CRM deal operations.</li>
              </ul>
            </div>
          </div>
        </Drawer>
    </div>
  );
};

export default Deals;